// src/features/settings/data/useSettingsRepo.js
import { useEffect, useMemo, useState } from "react";
import { useDb } from "../../../providers/DexieProvider";
import { createRepo } from "../../../db/repoFactory";
import { loadConfigByVersion } from "../../../db/schema/configLoader";
import { createEntity, coerceToModel } from "../../../db/entityFactory";

/**
 * resolveStoreVersion(db, storeName, override?)
 * ---------------------------------------------
 * Decides which version string to use for a store's config.
 * Priority:
 *   1) explicit override (if provided to the hook)
 *   2) store meta row inside the store itself (id === storeName) -> .version
 *   3) __schema meta store (id === storeName) -> .version
 * Throws if no version can be determined.
 *
 * @param {Dexie} db
 * @param {string} storeName   e.g., "settings"
 * @param {string|undefined} override  e.g., "1.1.2"
 * @returns {Promise<string>} semantic version "A.B.C"
 */
async function resolveStoreVersion(db, storeName, override) {
  if (override && /^\d+\.\d+\.\d+$/.test(String(override))) {
    return String(override);
  }
  // 1) Try store-local meta row
  try {
    const meta = await db.table(storeName).get(storeName);
    if (meta?.version && /^\d+\.\d+\.\d+$/.test(String(meta.version))) {
      return String(meta.version);
    }
  } catch (_) {}
  // 2) Fallback to __schema meta store
  try {
    const meta = await db.table("__schema").get(storeName);
    if (meta?.version && /^\d+\.\d+\.\d+$/.test(String(meta.version))) {
      return String(meta.version);
    }
  } catch (_) {}

  throw new Error(
    `resolveStoreVersion: could not determine version for "${storeName}". ` +
      `Provide an explicit { version } override.`
  );
}

/**
 * useSettingsRepo(options?)
 * -------------------------
 * Returns a repo for the "settings" store that:
 *  - Looks up the store's *effective* version (store meta → __schema), unless
 *    you pass an explicit override via options.version.
 *  - Loads that version's config with loadConfigByVersion().
 *  - Uses the config's dataObject model for shape-aware writes in set().
 *
 * @param {Object} [options]
 * @param {string} [options.version]       Explicit version override like "1.1.2"
 * @param {string} [options.folderExact]   Optional path hint for config loader
 *                                         (defaults to "features/settings/schema")
 * @returns {{ set(id,value,patch?):Promise<any>, getAll():Promise<object[]>, ...base }}
 */
export function useSettingsRepo(options = {}) {
  const { version: overrideVersion, folderExact = "features/settings/schema" } =
    options;
  const db = useDb();

  const [cfg, setCfg] = useState(null);
  const [cfgErr, setCfgErr] = useState(null);

  // Resolve version + load the normalized config for that version
  // useEffect(() => {
  //   let cancelled = false;
  //   (async () => {
  //     try {
  //       const v = await resolveStoreVersion(db, "settings", overrideVersion);
  //       const loaded = loadConfigByVersion(v, { folderExact });
  //       if (!cancelled) setCfg(loaded);
  //     } catch (err) {
  //       if (!cancelled) setCfgErr(err);
  //     }
  //   })();
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [db, overrideVersion, folderExact]);

  // Create the base repo once per DB instance
  const repo = useMemo(() => {
    const base = createRepo(db, "settings", {
      keyProp: "id",
      // If you prefer injection, you can pass the model here too once cfg is ready.
      // model: cfg?.model?.dataObject ?? null,
    });

    /**
     * set(id, value, patch?)
     * ----------------------
     * Model-aware upsert:
     *  - If config/model not yet loaded: minimal upsert (id, value, modified)
     *  - If model present:
     *      * existing → merge + coerceToModel → put
     *      * new      → createEntity(model, seed) → put
     */
    async function set(id, value, patch = {}) {
      const modified = Date.now();
      const table = base.table;

      // If the config hasn't loaded yet (component just mounted), do a minimal write.
      const dataModel = cfg?.model?.dataObject || null;
      const existing = await table.get(id);

      if (!dataModel) {
        const next = existing
          ? { ...existing, ...patch, value, modified }
          : { id, ...patch, value, modified };
        await base.put(next);
        return next.value;
      }

      if (existing) {
        const merged = { ...existing, ...patch, value, modified };
        const coerced = coerceToModel(dataModel, merged, {
          dropUnknown: false,
        });
        await base.put(coerced);
        return coerced.value;
      } else {
        const seed = { id, ...patch, value, modified };
        if (
          Array.isArray(dataModel.parents) &&
          dataModel.parents.length &&
          seed.parents == null
        ) {
          seed.parents = dataModel.parents.slice(); // e.g., ["settings"]
        }
        const created = createEntity(dataModel, seed, { required: ["id"] });
        await base.put(created);
        return created.value;
      }
    }

    // async function getAll() {
    //   return await base.table.orderBy("category").toArray();
    // }

    return {
      ...base,
      set,
      // getAll,
      // Expose the loaded config & version error for debugging/UX if desired
      _config: cfg,
      _configError: cfgErr,
    };
  }, [db, cfg, cfgErr]);

  return repo;
}

// previous chatgpt without dynamic load cnfg version
// // src/features/settings/data/useSettingsRepo.js
// import { useMemo } from "react";
// import { useDb } from "../../../providers/DexieProvider";
// import { createRepo } from "../../../db/repoFactory";
// import { loadConfigByVersion } from "../../../db/schema/configLoader";
// import { createEntity, coerceToModel } from "../../../db/schema/entityFactory";

// /**
//  * Load the normalized Settings config ONCE at module load.
//  * - Single source of truth for the model comes from this config file.
//  * - If you bump versions later, update the version here (or make it dynamic).
//  */
// const settingsCfg = loadConfigByVersion("1.1.1", {
//   folderExact: "features/settings/schema",
// });

// /**
//  * useSettingsRepo()
//  * -----------------
//  * React hook that returns a repository object for the "settings" table.
//  * It wraps a generic base repo with a model-aware `set()` that:
//  *  - For NEW records: builds a full object using the config's dataObject model
//  *    (via createEntity), then writes it.
//  *  - For EXISTING records: merges the patch/value, coerces via the model
//  *    (coerceToModel), then writes it.
//  *
//  * Returned API:
//  * - set(id: string, value: any, patch?: object) => Promise<any>
//  *     Upsert a record by id. `patch` can include extra fields like label/type/category.
//  *     Returns the final stored `value`.
//  * - getAll() => Promise<Array<object>>
//  *     Returns all settings ordered by `category`.
//  */
// export function useSettingsRepo() {
//   const db = useDb();

//   // Build once per DB instance so identity is stable across renders
//   const repo = useMemo(() => {
//     // const base = createRepo(db, "settings", { keyProp: "id" });
//     const base = createRepo(db, "settings", {
//       keyProp: "id",
//       model: settingsCfg?.model?.dataObject,
//     });

//     // Pull the data model from the normalized config (single source of truth)
//     const dataModel = settingsCfg?.model?.dataObject || null;

//     /**
//      * set(id, value, patch?)
//      * ----------------------
//      * Upsert a settings row by id. Model-aware if `dataModel` exists.
//      *
//      * @param {string} id      Stable id (e.g., "theme-accent")
//      * @param {any}    value   Value to set
//      * @param {object} [patch] Optional extra fields to write (label, type, category, etc.)
//      * @returns {Promise<any>} The final stored value
//      */
//     async function set(id, value, patch = {}) {
//       const modified = Date.now();
//       const table = base.table;

//       // Read current (if any)
//       const existing = await table.get(id);

//       // If no model provided in the config, fall back to minimal upsert
//       if (!dataModel) {
//         const next = existing
//           ? { ...existing, ...patch, value, modified }
//           : { id, ...patch, value, modified };
//         await base.put(next);
//         return next.value;
//       }

//       if (existing) {
//         // Merge and coerce to the config's dataObject model
//         const merged = { ...existing, ...patch, value, modified };
//         const coerced = coerceToModel(dataModel, merged); // keeps unknowns by default
//         await base.put(coerced);
//         return coerced.value;
//       } else {
//         // New record: seed minimal fields, then create via model template
//         const seed = { id, ...patch, value, modified };

//         // Optional convenience: if model defines parents and caller didn't provide them,
//         // auto-fill from the template (e.g., ["settings"])
//         if (
//           Array.isArray(dataModel.parents) &&
//           dataModel.parents.length &&
//           seed.parents == null
//         ) {
//           seed.parents = dataModel.parents.slice();
//         }

//         const created = createEntity(dataModel, seed, { required: ["id"] });
//         await base.put(created);
//         return created.value;
//       }
//     }

//     /**
//      * getAll()
//      * --------
//      * Return all settings, ordered by category.
//      * (Adjust the ordering/filtering here to match your UI needs.)
//      */
//     async function getAll() {
//       return await base.table
//         .orderBy("category")
//         // .filter((pref) => pref.id !== "settings")
//         .toArray();
//     }

//     return {
//       ...base,
//       set,
//       getAll,
//     };
//   }, [db]);

//   return repo;
// }

// previous
// import { useMemo } from "react";
// import { useDb } from "../../../providers/DexieProvider";
// import { createRepo } from "../../../db/repoFactory";
// import { loadConfigByVersion } from "../../../db/schema/configLoader";

// /**
//  * SettingsRepo provides an interface for storing and retrieving user settings
//  * in a Dexie-powered IndexedDB table named "settings". Setting values can be
//  * of any JSON-serializable type (boolean, number, string, object).
//  *
//  * @class
//  * @example
//  * const repo = new SettingsRepo(db);
//  * await repo.set('theme', 'dark');
//  * const theme = await repo.get('theme', 'light');
//  * const allPrefs = await repo.getAll();
//  */

// /**
//  * Generic CRUD helpers for a single table.
//  * You can extend per-feature with domain-specific helpers.
//  */
// export function useSettingsRepo() {
//   //, tableName, { keyProp = "id" } = {}) {
//   // const table = db.table(tableName);
//   const db = useDb();

//   // Build once per DB instance so identity is stable across renders
//   const repo = useMemo(() => {
//     const base = createRepo(db, "settings", { keyProp: "id" });

//     return {
//       ...base,
//       async set(id, value) {
//         const modified = Date.now();
//         await base.put({ id, value, modified });
//         return value;
//       },
//       // async get(id, defaultValue = null) {
//       //   const row = await base.table.get(id);
//       //   return row ? row.value : defaultValue;
//       // },
//       async getAll() {
//         return await base.table
//           .orderBy("category")
//           // .filter((pref) => pref.id !== "settings")
//           .toArray();
//       },
//     };
//   }, [db]);

//   return repo;
// }

// const settingsCfg = loadConfigByVersion("1.1.1", {
//   folderExact: "features/settings/schema",
// });

// // console.log(settingsCfg); // normalized config object (same shape as bulk loader output)
